/**
 * LevelScriptApi — the `engine.level` capability module (level loading for game scripts).
 *
 * Lets behaviours load / preload / unload levels and read preload progress — the API a splash
 * level uses to background-load the main level. `safe` tier: ordinary game scripts may switch
 * levels (like Unity's SceneManager), so it appears in the sandbox `SafeEngineApi`.
 *
 * Routes to the global {@link levelManager}, which delegates to the host (player LevelSubsystem /
 * editor) registered via `setLoaderHost`. In a context with no host registered (headless/tests),
 * the async methods reject with a clear error.
 *
 * `levelId` is a level asset GUID (the manifest key in the player; the asset's guid in the editor).
 */

import { scriptApi, scriptFn } from "../scriptApi";
import { levelManager, LoadLevelMode } from "../../LevelManager";

/** How a level is brought in (mirrors LoadLevelMode). Additive requires SoA segmentation (gated). */
export type LevelLoadModeName = "single" | "additive";

@scriptApi("level", { tier: "safe", version: 1 })
export class LevelScriptApi {
  /**
   * Load a level and switch to it. `single` (default) replaces the current level seamlessly
   * (shared assets stay resident); `additive` is reserved (throws until SoA segmentation lands).
   */
  @scriptFn({ async: true })
  load(levelId: string, mode: LevelLoadModeName = "single"): Promise<void> {
    return levelManager
      .loadLevel(levelId, mode === "additive" ? LoadLevelMode.Additive : LoadLevelMode.Single)
      .then(() => {});
  }

  /**
   * Preload a level in the background (load + acquire its assets) WITHOUT activating it. Pair with
   * {@link progress} for a loading bar, then {@link load} to swap in seamlessly. Splash-screen flow.
   */
  @scriptFn({ async: true })
  preload(levelId: string): Promise<void> {
    return levelManager.preloadLevel(levelId);
  }

  /** Preload progress for a level, 0..1 (1 once fully preloaded). */
  @scriptFn()
  progress(levelId: string): number {
    return levelManager.loadProgress(levelId);
  }

  /** Unload a level and release its assets (those not still referenced by another loaded level). */
  @scriptFn({ async: true })
  unload(levelId: string): Promise<void> {
    return levelManager.unloadLevelById(levelId);
  }

  /** GUID of the currently-active level, or null. */
  @scriptFn()
  current(): string | null {
    return levelManager.activeLevel?.guid ?? null;
  }
}
